<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // STYLING
    import 'tailwindcss/tailwind.css';

    // CASSIOPEIA
    import {
        addMessage,
        AuctionItem,
        BidForm,
        Button,
        Card,
        Heading,
        ProgressLines,
        SESSION_ORGANIZATION,
    } from '@smatch/cassiopeia-web-components';

    // TYPES
    import type { Bid, FormComponentLegacy, Organization } from 'src/app/types';

    // COMPONENTS
    import BidUpdateAside from './components/BidUpdateAside.svelte';
    import BidUpdateShipping from './components/BidUpdateShipping.svelte';
    import BidUpdateOrganization from './components/BidUpdateOrganization.svelte';
    import BidUpdatePayment from './components/BidUpdatePayment.svelte';
    import BidUpdateInvoiceAddress from './components/BidUpdateInvoiceAddress.svelte';

    /* TYPES ----------------------------------------------------------------------------------- */

    type OrganizationWithLegal = Organization & Required<Pick<Organization, 'legal'>>;

    /* PROPS ----------------------------------------------------------------------------------- */

    export let bid: Bid | undefined = undefined;

    /* INITIALIZE ------------------------------------------------------------------------------ */

    const FORMS: Record<string, FormComponentLegacy> = {};
    let organization = { legal: {}, ...$SESSION_ORGANIZATION } as OrganizationWithLegal;

    const STEPS = {
        step1: 'Offer Received',
        step2: 'Offer Review',
        step3: 'Availability Check',
        step4: 'Order Confirmation',
        step5: 'Shipping / Pick-Up',
    };

    let hasShipping;
    let hasServices;

    const onClick = async (): Promise<void> => {

        const IS_VALID = Object.values(FORMS).every(form => form.isValid());

        if (IS_VALID) {
            addMessage({ heading: 'Offer updated successfully!', type: 'success' });
        }

        await Promise.all(Object.values(FORMS).map(async component => {
            await component.submit();
        }));
    };

</script>

{#if bid !== undefined }

    <div class = 's8-container'>

        <Heading>Your Offer</Heading>

        <div class = 's8-progress'>
            <ProgressLines steps = { STEPS } current = 'step2' mode = 'light' />
        </div>

        <div class = 's8-wrap'>

            <section>

                <BidUpdateOrganization bind:this = { FORMS.organization } bind:organization />

                <BidUpdateInvoiceAddress
                    organizationKey = { bid.buyer.organizationKey }
                    bind:this = { FORMS.invoice_address }
                />

                <BidForm { bid } action = 'update' bind:this = { FORMS.bid }>

                    <div class = 's8-subsection'>

                        <BidUpdatePayment
                            bind:financeOptionCode = { bid.buyer.financeOptionCode }
                            bind:organization
                            bind:this = { FORMS.payment }
                        />

                        <AuctionItem key = { bid.auctionKey } let:auction>
                            <BidUpdateShipping
                                { auction }
                                bidKey = { bid._.key }
                                bind:addressKey = { bid.buyer.addressKey }
                                bind:code = { bid.buyer.shippingOptionCode }
                                bind:hasShipping
                                bind:hasServices
                                bind:logisticalServices = { bid.buyer.logisticalServices }
                                bind:this = { FORMS.shipping }
                            />
                        </AuctionItem>

                    </div>

                </BidForm>

                <Button
                    label = 'Update Offer'
                    color = 'primary'
                    size = 'lg'
                    css = 's8-submit-button'
                    on:click = { onClick }
                />

            </section>

            <Card color = 'light' style = 'ghost'>
                <BidUpdateAside { bid } { hasShipping } { hasServices }/>
            </Card>

        </div>

    </div>

{/if}

<style lang='postcss'>

    div.s8-container {
        container-type: inline-size;
        container-name: bid-update;

        div.s8-wrap {
            display: flex;
            gap: 40px;

            :global(> div.s8-card) {
                width: 280px;
                margin-bottom: 0px !important;
            }

            section,
            div.s8-subsection {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 20px;
            }

            :global(button.s8-submit-button) {
                margin-top: 20px;
            }
        }
    }

    @container bid-update (max-width: 600px) {
        div.s8-wrap {
            flex-direction: column;

            :global(> div.s8-card) {
                width: 100% !important;
            }
        }

        div.s8-progress {
            display: none !important;
        }
    }
</style>